import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Signal, AfterViewInit } from '@angular/core';
import { APIService, KPI } from '../../../../@core/api';
import { NbDialogService, NbAccordionModule, NbButtonModule } from '@nebular/theme';
import { AddConsumptionDialogComponent } from '../add-consumption-dialog/add-consumption-dialog.component';
import { ChartsService, UsersService, SaveToastService } from '../../../../@core/utils';
import { first, Subscription } from 'rxjs';
import { ThousandsSeparatorPipe } from '../../../pipes/thousands-separator.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GROUP_PERMISSION_EDIT_ENERGY_CONSUMPTION } from '../../../../@core/api/models';
import { BasicDialogComponent } from '../../basic-dialog/basic-dialog.component';

@Component({
  selector: 'app-site-kwh-consumption',
  templateUrl: './site-kwh-consumption.component.html',
  styleUrls: ['./site-kwh-consumption.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NbAccordionModule,
    NbButtonModule,
    TranslateModule,
    ThousandsSeparatorPipe,
    
  ],
})
export class SiteKwhConsumptionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() multiPerimeter: KPI.Perimeter;
  @Input() readOnly: boolean = false;

  public energyTypeConsumptionsList: KPI.PerimeterTrajectoryConsumption[][] = [];
  public allConsumptionsList: KPI.PerimeterTrajectoryConsumption[] = [];
  private subscriptions: Subscription[] = [];
  public hasTrajectories: boolean;
  public isAccordionExpanded: boolean = true;
  public canAddConsumption: Signal<boolean> = toSignal(
    this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_ENERGY_CONSUMPTION])
  );
  public loading: boolean = false

  constructor(
    private dialogService: NbDialogService,
    private chartsService: ChartsService,
    private cdRef: ChangeDetectorRef,
    private usersService: UsersService,
    private translate: TranslateService,
    private api: APIService,
    private saveToast: SaveToastService,
  ) { }

  public ngOnInit(): void {
    this.loading = true;
    this.subscriptions.push(
      this.chartsService.getSiteConsumption(this.multiPerimeter.id).subscribe((siteConsumption) => {
        this.hasTrajectories = siteConsumption.perimeters.some(perimeter => perimeter.trajectories && perimeter.trajectories.length > 0);
        const energyTypeConsumptionsMap: { [key: string]: KPI.PerimeterTrajectoryConsumption[] } = {};
        this.energyTypeConsumptionsList = [];
        this.allConsumptionsList = [];
        siteConsumption?.perimeters.filter((perimeter) =>
          perimeter.trajectories?.filter((trajectory) =>
            trajectory.energy_type
          ).map((trajectory) => {
            trajectory.perimeter_consumptions.filter((consumption) =>
              consumption.consumption !== null
              || consumption.consumption_corrected_climate !== null
              || consumption.consumption_corrected_official !== null,
            ).sort((a, b) => b.year - a.year).map((consumption) => {
              if (!energyTypeConsumptionsMap[trajectory.energy_type.id]) {
                energyTypeConsumptionsMap[trajectory.energy_type.id] = [];
              }

              const newElt = {
                ...consumption,
                perimeter: perimeter,
                energy_type: trajectory.energy_type,
              }
              energyTypeConsumptionsMap[trajectory.energy_type.id].push(newElt);
              this.allConsumptionsList.push(newElt);
            });
          }),
        );
        this.energyTypeConsumptionsList = Object.values(energyTypeConsumptionsMap);
        this.loading = false;
        // Group allConsumptionsList elements by perimeter.id and year, and sum the consumption values
        const allConsumptionsListGrouped = this.allConsumptionsList.reduce((acc, consumption) => {
          const key = `${consumption.perimeter.id}-${consumption.year}`;
          if (!acc[key]) {
            acc[key] = {
              ...consumption,
              consumption: 0,
              consumption_corrected_climate: 0,
              consumption_corrected_official: 0,
            };
          }
          acc[key].consumption += consumption.consumption || 0;
          acc[key].consumption_corrected_climate += consumption.consumption_corrected_climate || 0;
          acc[key].consumption_corrected_official += consumption.consumption_corrected_official || 0;
          return acc;
        }, {});
        this.allConsumptionsList = Object.values(allConsumptionsListGrouped);

        this.cdRef.detectChanges()
      }),
    );
  }

  public openAddConsumptionDialog(
    consumption?: KPI.PerimeterTrajectoryConsumption,
  ): void {
    if (this.canAddConsumption()) {
      this.dialogService.open(
        AddConsumptionDialogComponent,
        {
          autoFocus: false,
          context: {
            energyType: consumption?.energy_type,
            multiPerimeter: this.multiPerimeter,
          },
        },
      ).onClose.subscribe((result) => {
        if (result) {
          this.saveToast.showSaved();
        }
      });
    }
  }

  public onAccordionToggle(expanded: boolean): void {
    this.isAccordionExpanded = expanded;
    this.cdRef.detectChanges();
  }

  public toggleAccordion(): void {
    this.isAccordionExpanded = !this.isAccordionExpanded;
    this.cdRef.detectChanges();
  }

  public ngAfterViewInit(): void {
    this.isAccordionExpanded = true;
    this.cdRef.detectChanges();
  }

  public editConsumption(consumption: KPI.PerimeterTrajectoryConsumption): void {
    if (this.canAddConsumption()) {
      this.dialogService.open(
        AddConsumptionDialogComponent,
        {
          autoFocus: false,
          context: {
            energyType: consumption.energy_type,
            multiPerimeter: this.multiPerimeter,
            consumption: consumption,
            isEditing: true,
          },
        },
      ).onClose.subscribe((result) => {
        if (result) {
          this.saveToast.showSaved();
        }
      });
    }
  }

  public deleteConsumption(consumption: KPI.PerimeterTrajectoryConsumption): void {
    if (this.canAddConsumption()) {
      this.dialogService.open(
        BasicDialogComponent,
        {
          autoFocus: false,
          context: {
            title: this.translate.instant('Consumption deletion'),
            content: this.translate.instant('Are you sure you want to delete this consumption ?'),
          },
        },
      ).onClose.subscribe((result) => {
        if (result) {
          this.saveToast.showSaving();
          this.api.delete_year_consumption(consumption.id)
            .pipe(first())
            .subscribe(() => {
              this.chartsService.declaredConsumptionsHasChanged();
              this.saveToast.showSaved();
            });
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

}
